import { Audience } from '@/domain/types/audience';
import { User } from '@/domain/types/user';
import {
  listAudiences,
  listMembers,
  removeMember,
} from '@/domain/use-cases/audiences/audience-service';
import {
  filterUsers,
  MemberSearchField,
  sortByFullName,
} from '@/domain/use-cases/audiences/member-list';
import Button from '@/presentation/components/button';
import Drawer from '@/presentation/components/drawer';
import Table, { Column } from '@/presentation/components/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiArrowLeft, FiPlus, FiUserMinus, FiUsers } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ActionButton from '../components/action-button';
import ListSkeleton from '../components/list-skeleton';
import AddMembersPanel from './add-members-panel';
import { plural, searchOptions } from './search-options';

const AudienceMembersPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [audience, setAudience] = useState<Audience | null>(null);
  const [members, setMembers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchField, setSearchField] = useState<MemberSearchField>('document');
  const [searchTerm, setSearchTerm] = useState('');
  // Quitar pide una confirmación en la misma fila antes de escribir.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const [audiences, audienceMembers] = await Promise.all([
        listAudiences(),
        listMembers(id),
      ]);
      setAudience(audiences.find((a) => a.id === id) ?? null);
      setMembers(sortByFullName(audienceMembers));
    } catch (error) {
      toast.error('Error al cargar los datos de la audiencia', {
        theme: 'colored',
      });
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const memberIds = useMemo(
    () => new Set(members.map((member) => member.id)),
    [members],
  );

  const visibleMembers = useMemo(
    () => filterUsers(members, searchField, searchTerm),
    [members, searchField, searchTerm],
  );

  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  const handleAdded = (added: User[]) => {
    const addedIds = new Set(added.map((user) => user.id));
    setMembers((prev) =>
      sortByFullName([...prev.filter((m) => !addedIds.has(m.id)), ...added]),
    );
  };

  const handleRemove = async (user: User) => {
    if (!id) return;
    setRemovingId(user.id);
    try {
      await removeMember(user.id, id);
      setMembers((prev) => prev.filter((m) => m.id !== user.id));
      toast.success(`${user.name} ${user.lastname} ya no es miembro`, {
        theme: 'colored',
      });
    } catch (error) {
      toast.error('Error al quitar al miembro', { theme: 'colored' });
    } finally {
      setRemovingId(null);
      setConfirmingId(null);
    }
  };

  const columns: Column<User>[] = [
    {
      header: 'Nombre',
      accessor: 'name',
      render: (_value, user) => (
        <span className="text-sm font-medium text-gray-900">
          {user.name} {user.lastname}
        </span>
      ),
    },
    { header: 'Documento', accessor: 'document' },
    {
      header: 'Email',
      accessor: 'email',
      render: (value) => <span className="normal-case">{value as string}</span>,
    },
    { header: 'Área', accessor: 'area' },
    {
      header: 'Acciones',
      accessor: 'id',
      render: (_value, user) =>
        confirmingId === user.id ? (
          <div className="flex flex-wrap items-center justify-center gap-2">
            <span className="text-sm text-gray-700">
              ¿Quitar de la audiencia?
            </span>
            <ActionButton
              label="Sí, quitar"
              tone="brand"
              icon={<FiUserMinus size={14} aria-hidden="true" />}
              disabled={removingId === user.id}
              onClick={() => handleRemove(user)}
            />
            <ActionButton
              label="Cancelar"
              tone="neutral"
              icon={null}
              disabled={removingId === user.id}
              onClick={() => setConfirmingId(null)}
            />
          </div>
        ) : (
          <div className="flex justify-center">
            <ActionButton
              label="Quitar"
              tone="danger"
              icon={<FiUserMinus size={14} aria-hidden="true" />}
              onClick={() => setConfirmingId(user.id)}
            />
          </div>
        ),
    },
  ];

  const openDrawer = () => setIsDrawerOpen(true);

  return (
    <div className="flex flex-col gap-6 p-4">
      <div>
        <button
          type="button"
          onClick={() => navigate('/dashboard/audiences')}
          className="mb-3 inline-flex items-center gap-1.5 rounded text-sm text-gray-600 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <FiArrowLeft aria-hidden="true" />
          Volver a audiencias
        </button>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Miembros{audience ? ` de "${audience.name}"` : ''}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {isLoading
                ? 'Cargando miembros…'
                : `${members.length} ${plural(members.length, 'miembro', 'miembros')}`}
            </p>
          </div>
          <Button
            label="Agregar miembros"
            icon={<FiPlus />}
            onClick={openDrawer}
            className="shadow-sm"
          />
        </div>
      </div>

      {isLoading ? (
        <ListSkeleton label="Cargando miembros" />
      ) : members.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-primary">
            <FiUsers size={22} aria-hidden="true" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            Esta audiencia aún no tiene miembros
          </h2>
          <p className="max-w-sm text-sm text-gray-600">
            Agrega usuarios para que vean las publicaciones dirigidas a esta
            audiencia.
          </p>
          <Button
            label="Agregar miembros"
            icon={<FiPlus />}
            onClick={openDrawer}
            className="mt-2"
          />
        </div>
      ) : (
        <>
          <div className="flex max-w-xl gap-2">
            <select
              value={searchField}
              onChange={(e) =>
                setSearchField(e.target.value as MemberSearchField)
              }
              aria-label="Filtrar por"
              className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {searchOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Filtrar miembros..."
              aria-label="Filtrar miembros"
              className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {visibleMembers.length === 0 ? (
            <p className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-600">
              Ningún miembro coincide con el filtro.
            </p>
          ) : (
            <Table
              columns={columns}
              data={visibleMembers}
              className="rounded-lg"
            />
          )}
        </>
      )}

      <Drawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        title="Agregar miembros"
        dismissible
        widthClass="w-full md:w-2/3 lg:w-1/2 xl:w-2/5"
      >
        {isDrawerOpen && id && (
          <AddMembersPanel
            audienceId={id}
            memberIds={memberIds}
            onClose={closeDrawer}
            onAdded={handleAdded}
          />
        )}
      </Drawer>
    </div>
  );
};

export default AudienceMembersPage;
